/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Shop } from '../services/shopService';
import { Product } from '../services/api';
import { apiService } from '../services/api';
import './ShopSelectionModal.css';

interface ShopWithProduct {
  shop: Shop;
  product: Product;
  price: number;
  distance?: number;
}

interface ShopSelectionModalProps {
  isOpen: boolean;
  product: Product | null;
  onClose: () => void;
  onSelectShop: (shop: Shop, product: Product) => void;
}

type SortOption = 'rating' | 'distance' | 'price-low' | 'price-high';

const ShopSelectionModal: React.FC<ShopSelectionModalProps> = ({
  isOpen,
  product,
  onClose,
  onSelectShop
}) => {
  const [shops, setShops] = useState<ShopWithProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('rating');
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);

  // Fetch shops that have this product
  useEffect(() => {
    if (isOpen && product) {
      fetchShopsWithProduct();
    } else {
      setShops([]);
      setSelectedShopId(null);
    }
  }, [isOpen, product]);

  const fetchShopsWithProduct = async () => {
    if (!product) return;

    setIsLoading(true);
    try {
      // Use the API to search shops by product
      const results = await apiService.searchShopsByProduct(product.name);
      
      // Convert to ShopWithProduct format
      const shopsWithProduct: ShopWithProduct[] = results.map((result: any) => ({
        shop: result.shop,
        product: result.product || product,
        price: result.price || result.product?.price || product.price,
        distance: result.distance
      }));

      setShops(shopsWithProduct);
      
      // Default select the shop with highest rating
      if (shopsWithProduct.length > 0) {
        const bestRatedShop = [...shopsWithProduct].sort((a, b) => 
          (b.shop.rating || 0) - (a.shop.rating || 0)
        )[0];
        setSelectedShopId(bestRatedShop.shop.id);
      }
    } catch (error) {
      console.error('Error fetching shops with product:', error);
      setShops([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Sort shops based on selected option
  const sortedShops = useMemo(() => {
    if (!shops.length) return [];

    const sorted = [...shops];
    
    switch (sortBy) {
      case 'rating':
        sorted.sort((a, b) => (b.shop.rating || 0) - (a.shop.rating || 0));
        break;
      case 'distance':
        sorted.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
        break;
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price);
        break;
    }
    
    return sorted;
  }, [shops, sortBy]);

  const handleSelectShop = (shop: Shop, productFromShop: Product) => {
    // Use the original product passed to modal (more complete) instead of shop's product variant
    // The shop's product might be missing fields or have different structure
    const productToAdd = product || productFromShop;
    
    if (!productToAdd) {
      console.error('❌ ShopSelectionModal: No product available to add to cart');
      alert('Error: Product information is missing. Please try again.');
      return;
    }
    
    console.log('🛒 ShopSelectionModal: Adding product to cart:', {
      originalProduct: product?.id,
      shopProduct: productFromShop?.id,
      usingProduct: productToAdd.id,
      shopId: shop.id
    });
    
    onSelectShop(shop, productToAdd);
    onClose();
  };

  if (!isOpen || !product) return null;

  return (
    <div className="shop-selection-modal-overlay" onClick={onClose}>
      <div className="shop-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="shop-selection-modal-header">
          <h2>Select a Shop</h2>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="shop-selection-product-info">
          <h3>{product.name}</h3>
          {product.brand && <p className="product-brand">Brand: {product.brand}</p>}
          <p className="product-price">₹{product.price}</p>
        </div>

        {isLoading ? (
          <div className="shop-selection-loading">
            <p>Loading available shops...</p>
          </div>
        ) : shops.length === 0 ? (
          <div className="shop-selection-empty">
            <p>No shops found with this product.</p>
          </div>
        ) : (
          <>
            <div className="shop-selection-sort">
              <label>Sort by:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)}>
                <option value="rating">Rating (High to Low)</option>
                <option value="distance">Distance (Near to Far)</option>
                <option value="price-low">Price (Low to High)</option>
                <option value="price-high">Price (High to Low)</option>
              </select>
            </div>

            <div className="shop-selection-list">
              {sortedShops.map((shopWithProduct) => {
                const shop = shopWithProduct.shop;
                const isSelected = selectedShopId === shop.id;
                
                return (
                  <div
                    key={shop.id}
                    className={`shop-selection-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedShopId(shop.id)}
                  >
                    <div className="shop-selection-item-content">
                      <div className="shop-logo">
                        {shop.logo ? (
                          <img src={shop.logo} alt={shop.name} />
                        ) : (
                          <div className="shop-logo-placeholder">🏪</div>
                        )}
                      </div>
                      <div className="shop-info">
                        <h4>{shop.name}</h4>
                        <p className="shop-address">{shop.address}</p>
                        <div className="shop-meta">
                          {shop.rating && (
                            <span className="shop-rating">
                              ⭐ {shop.rating.toFixed(1)} 
                              {shop.total_reviews && ` (${shop.total_reviews} reviews)`}
                            </span>
                          )}
                          {shopWithProduct.distance !== undefined && (
                            <span className="shop-distance">
                              📍 {shopWithProduct.distance.toFixed(1)} km
                            </span>
                          )}
                        </div>
                        <div className="shop-price">
                          <span className="price-label">Price:</span>
                          <span className="price-value">₹{shopWithProduct.price}</span>
                        </div>
                      </div>
                      <div className="shop-selection-radio">
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => setSelectedShopId(shop.id)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="shop-selection-actions">
              <button className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn-add-to-cart"
                onClick={() => {
                  const selectedShop = sortedShops.find(s => s.shop.id === selectedShopId);
                  if (selectedShop) {
                    handleSelectShop(selectedShop.shop, selectedShop.product);
                  }
                }}
                disabled={!selectedShopId}
              >
                Add to Cart
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShopSelectionModal;








